import { useMemo } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useIntl } from 'dibs-react-intl';
import { formatRelativeDate } from 'dibs-relative-date-format/exports/formatRelativeDate';

import { getPriceFromAmountListByCurrency } from '../helpers';

import { type useItemPriceData_item$key } from './__generated__/useItemPriceData_item.graphql';
import { type useItemPriceData_displayPrice$key } from './__generated__/useItemPriceData_displayPrice.graphql';

const itemFragment = graphql`
    fragment useItemPriceData_item on Item {
        activeHold {
            displayTimeToExpire
        }
    }
`;

const displayPriceFragment = graphql`
    fragment useItemPriceData_displayPrice on ItemDisplayPriceType @relay(plural: true) {
        textType
        amountType
        # eslint-disable-next-line relay/unused-fields
        originalConvertedAmountList {
            currency
            amount
        }
        # eslint-disable-next-line relay/unused-fields
        convertedAmountList {
            currency
            amount
        }
        percentageOff
        # eslint-disable-next-line relay/unused-fields
        privateOfferConvertedAmountList {
            currency
            amount
        }
        privateOfferPercentageOff
        offerExpirationDate
        quantityDisplay
        isVatIncluded
        priceBookName
        saleEndDate
    }
`;

const isNetOrSale = (value?: string | null): value is 'NET' | 'WEEKLY_SALE' =>
    !!value && ['NET', 'WEEKLY_SALE'].includes(value);
const isSoldOrUnavailable = (value?: string | null): value is 'SOLD' | 'UNAVAILABLE' =>
    !!value && ['SOLD', 'UNAVAILABLE'].includes(value);

type HoldType = 'HOLD' | 'HOLD_CUSTOM' | null;
const isHold = (value?: string | null): value is HoldType =>
    !!value && ['HOLD', 'HOLD_CUSTOM'].includes(value);

type PrivateOfferOriginaPrice =
    | { type: 'RETAIL' | 'WEEKLY_SALE'; amount: number }
    | { type: 'PRICE_UPON_REQUEST'; amount: null }
    | { type: 'NET'; amount: number | null };

type Return =
    | {
          currentPrice: { type: 'PRICE_UPON_REQUEST'; amount: null };
          originalPrice: null;
          quantityLabel: null;
          percentageOff: null;
          vatIncluded: null;
          priceBookName: null;
          privateOfferExpiration: null;
          holdType: null;
          holdExpiration: null;
      }
    | {
          currentPrice: {
              type: 'SOLD' | 'UNAVAILABLE';
              amount: number | null;
          };
          originalPrice: {
              type: null;
              amount: number | null;
          };
          quantityLabel: null;
          percentageOff: null;
          vatIncluded: null;
          priceBookName: null;
          privateOfferExpiration: null;
          holdType: null;
          holdExpiration: null;
      }
    | {
          currentPrice: {
              type: 'RETAIL';
              amount: number;
          };
          originalPrice: null;
          quantityLabel: string | null;
          percentageOff: null;
          vatIncluded: boolean;
          priceBookName: string | null;
          privateOfferExpiration: null;
          holdType: HoldType;
          holdExpiration: string | null;
      }
    | {
          currentPrice: {
              type: 'NET' | 'WEEKLY_SALE';
              amount: number;
          };
          originalPrice: {
              type: 'RETAIL';
              amount: number;
          };
          quantityLabel: string | null;
          percentageOff: number | null;
          vatIncluded: boolean;
          priceBookName: string | null;
          privateOfferExpiration: null;
          holdType: HoldType;
          holdExpiration: string | null;
          saleEndDate: string | null;
      }
    | {
          currentPrice: {
              type: 'PRIVATE_OFFER';
              amount: number;
          };
          originalPrice: PrivateOfferOriginaPrice;
          quantityLabel: string | null;
          percentageOff: number | null;
          vatIncluded: boolean;
          priceBookName: string | null;
          privateOfferExpiration: string | null;
          holdType: HoldType;
          holdExpiration: string | null;
      }
    | null;

export const useItemPriceData = ({
    item: itemRef,
    displayPrice: displayPriceRef,
    currency,
}: {
    item?: useItemPriceData_item$key | null;
    displayPrice?: useItemPriceData_displayPrice$key | null;
    currency: string;
}): Return => {
    const item = useFragment(itemFragment, itemRef);
    const displayPrice = useFragment(displayPriceFragment, displayPriceRef);
    const price = displayPrice?.[0];

    const intl = useIntl();

    return useMemo(() => {
        if (!price || !item) {
            return null;
        }

        const {
            originalConvertedAmountList,
            convertedAmountList,
            privateOfferConvertedAmountList,
            offerExpirationDate,
            quantityDisplay: quantityLabel,
            isVatIncluded,
            priceBookName,
            amountType,
            textType,
            saleEndDate,
        } = price;

        const originalPrice =
            getPriceFromAmountListByCurrency(originalConvertedAmountList, currency) ?? null;
        const currentPrice =
            getPriceFromAmountListByCurrency(convertedAmountList, currency) ?? null;
        const percentageOff = Number(price.percentageOff);

        const privateOfferPrice =
            getPriceFromAmountListByCurrency(privateOfferConvertedAmountList, currency) ?? null;
        const privateOfferExpiration = offerExpirationDate
            ? formatRelativeDate(intl, offerExpirationDate)
            : null;
        const privateOfferPercentageOff = Number(price.privateOfferPercentageOff);

        const vatIncluded = isVatIncluded || false;

        const holdType = isHold(textType) ? textType : null;
        const holdExpiration = item?.activeHold?.displayTimeToExpire || null;

        let data: Return = null;

        if (isSoldOrUnavailable(textType)) {
            // sold or unavailable can't have a private offer price because of that we return here
            return {
                currentPrice: { type: textType, amount: currentPrice },
                originalPrice: { type: null, amount: originalPrice },
                quantityLabel: null,
                percentageOff: null,
                vatIncluded: null,
                priceBookName: null,
                privateOfferExpiration: null,
                holdType: null,
                holdExpiration: null,
            };
        } else if (textType === 'PUR') {
            data = {
                currentPrice: { type: 'PRICE_UPON_REQUEST', amount: null },
                originalPrice: null,
                quantityLabel: null,
                percentageOff: null,
                vatIncluded: null,
                priceBookName: null,
                privateOfferExpiration: null,
                holdType: null,
                holdExpiration: null,
            };
        } else if (amountType === 'RETAIL' && currentPrice) {
            data = {
                currentPrice: { type: 'RETAIL', amount: currentPrice },
                originalPrice: null,
                quantityLabel,
                percentageOff: null,
                vatIncluded,
                priceBookName,
                privateOfferExpiration: null,
                holdType,
                holdExpiration,
            };
        } else if (isNetOrSale(amountType) && currentPrice && originalPrice) {
            data = {
                currentPrice: { type: amountType, amount: currentPrice },
                originalPrice: { type: 'RETAIL', amount: originalPrice },
                quantityLabel,
                percentageOff,
                vatIncluded,
                priceBookName,
                privateOfferExpiration: null,
                holdType,
                holdExpiration,
                saleEndDate,
            };
        }

        if (privateOfferPrice && data) {
            return {
                currentPrice: { type: 'PRIVATE_OFFER', amount: privateOfferPrice },
                originalPrice:
                    // for Net or Sale items we want the original price to be the item list price not the current price NET, WEEKLY_SALE price
                    isNetOrSale(amountType) && data.originalPrice
                        ? data.originalPrice
                        : data.currentPrice,
                quantityLabel,
                percentageOff: privateOfferPercentageOff,
                vatIncluded,
                priceBookName,
                privateOfferExpiration,
                holdType,
                holdExpiration,
            };
        }

        return data;
    }, [currency, item, price, intl]);
};
